// Accessibility option + statement — the IS 5568 / WCAG-support widget.
//
// Israeli law (IS 5568, adopting WCAG 2.0 AA) requires every business-facing site to
// carry BOTH an accessibility STATEMENT and an accessibility OPTION (an on-page control
// that lets a visitor adjust the presentation). Pomela apps serve Israeli businesses and
// inherit that duty; the platform itself is a business site and carries it too.
//
// ONE canonical implementation, two delivery forms:
//   • the platform (creator) imports `mountA11yWidget` and calls it — normal bundled code,
//     CSP-safe under `script-src 'self'`, no eval.
//   • the generation worker serializes the SAME function via `buildA11yWidgetScript` into a
//     first-party `a11y.js` asset injected into every built app.
// Because mountA11yWidget is fully self-contained (everything it needs arrives in `cfg`),
// .toString() serialization reproduces it exactly — the platform and the apps it makes
// behave identically, from one source.
//
// Controls (each persisted, reversible, keyboard-operable): text size, high contrast,
// underline links, readable font, pause animations, large cursor, reset all, + the
// accessibility statement inline. Honors focus-visible, Esc-to-close, focus return,
// aria-expanded/dialog, RTL from <html dir>, and prefers-reduced-motion.

export interface A11yWidgetOptions {
  contactEmail: string;
  siteName?: string;
  lang?: 'he' | 'en';
  /**
   * 'auto'  — show the trigger button on load (Pomela's own public platform).
   * 'optin' — ship the widget but keep it hidden until the visitor opts in
   *           (persisted flag, `window.pomelaA11y.enable()`, or Alt+Shift+A).
   *           The default for GENERATED apps: the builder owns each app's a11y duty.
   */
  mode?: 'auto' | 'optin';
}

/** The applied-settings CSS. Separate export so a guard can assert the levers exist. */
export const A11Y_WIDGET_CSS = `
:root{--pom-a11y-fscale:1}
html[data-pom-a11y-font="sans"] body{font-family:system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif !important}
html[data-pom-a11y-contrast="on"]{filter:contrast(1.35) saturate(1.1)}
html[data-pom-a11y-contrast="on"] body{background:#fff !important;color:#000 !important}
html[data-pom-a11y-links="on"] a{text-decoration:underline !important;text-underline-offset:2px}
html[data-pom-a11y-motion="off"] *{animation:none !important;transition:none !important;scroll-behavior:auto !important}
html[data-pom-a11y-cursor="on"],html[data-pom-a11y-cursor="on"] *{cursor:url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path d="M6 2l20 12-8 2-2 8z" fill="black" stroke="white" stroke-width="1.5"/></svg>') 4 2,auto}
html[data-pom-a11y-fscale] body{font-size:calc(100% * var(--pom-a11y-fscale))}
.pom-a11y-btn{position:fixed;bottom:16px;inset-inline-end:16px;z-index:2147483000;width:52px;height:52px;border-radius:50%;border:2px solid #fff;background:#0b57d0;color:#fff;box-shadow:0 4px 14px rgba(0,0,0,.3);cursor:pointer;display:grid;place-items:center;padding:0}
.pom-a11y-btn:focus-visible{outline:3px solid #ffbf00;outline-offset:2px}
.pom-a11y-btn svg{width:28px;height:28px}
.pom-a11y-panel{position:fixed;bottom:80px;inset-inline-end:16px;z-index:2147483000;width:min(320px,calc(100vw - 32px));max-height:min(70vh,560px);overflow:auto;background:#fff;color:#111;border:1px solid #cdd;border-radius:14px;box-shadow:0 12px 40px rgba(0,0,0,.35);padding:14px;font:15px/1.5 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif}
.pom-a11y-panel[hidden]{display:none}
.pom-a11y-panel h2{font-size:16px;margin:0 0 8px}
.pom-a11y-row{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:8px 4px;border-top:1px solid #eef}
.pom-a11y-row:first-of-type{border-top:0}
.pom-a11y-row button,.pom-a11y-toggle{min-height:44px;min-width:44px;border-radius:10px;border:1px solid #b9c;background:#f4f6fb;color:#111;cursor:pointer;padding:6px 12px;font:inherit}
.pom-a11y-toggle[aria-pressed="true"]{background:#0b57d0;color:#fff;border-color:#0b57d0}
.pom-a11y-size{display:inline-flex;gap:6px}
.pom-a11y-reset{margin-top:10px;width:100%;background:#fff;border:1px solid #b9c}
.pom-a11y-statement{margin-top:12px;font-size:13px;color:#333}
.pom-a11y-statement summary{cursor:pointer;font-weight:600;color:#0b57d0;min-height:44px;display:flex;align-items:center}
.pom-a11y-statement p{margin:6px 0}
`.trim();

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

/** The accessibility statement (concise IS-5568 form). Also servable at a stable URL. */
export function a11yStatementHtml(opts: A11yWidgetOptions): string {
  const he = opts.lang === 'he';
  const name = opts.siteName || 'this site';
  return he
    ? `<h1>הצהרת נגישות</h1>
<p>אנו רואים חשיבות רבה במתן שירות שוויוני ופועלים להנגיש את ${esc(name)} בהתאם לתקן הישראלי ת"י 5568 (WCAG 2.0 רמה AA).</p>
<p>האתר כולל כלי נגישות (הכפתור בפינת המסך) לשינוי גודל טקסט, ניגודיות, הדגשת קישורים, עצירת אנימציות ועוד, ונבנה לתמיכה בניווט מקלדת ובקוראי מסך.</p>
<p>נתקלתם בבעיה? נשמח לתקן — פנו אלינו: <a href="mailto:${esc(opts.contactEmail)}">${esc(opts.contactEmail)}</a>.</p>`
    : `<h1>Accessibility statement</h1>
<p>We are committed to equal access and work to make ${esc(name)} accessible per Israeli Standard IS 5568 (WCAG 2.0 level AA).</p>
<p>This site includes an accessibility tool (the corner button) to adjust text size, contrast, link emphasis, motion and more, and is built to support keyboard navigation and screen readers.</p>
<p>Found a barrier? We want to fix it — contact us at <a href="mailto:${esc(opts.contactEmail)}">${esc(opts.contactEmail)}</a>.</p>`;
}

interface WidgetCfg {
  email: string;
  site: string;
  lang: string;
  mode: 'auto' | 'optin';
  css: string;
  icon: string;
  stmtHe: string;
  stmtEn: string;
}

/**
 * The canonical widget. Fully self-contained (no external references) so it serializes
 * cleanly for the generated-app asset. `win` is the target window.
 */
export function mountA11yWidget(win: Window, cfg: WidgetCfg): void {
  const w = win as unknown as { __pomA11yWidget?: number };
  if (w.__pomA11yWidget) return;
  w.__pomA11yWidget = 1;
  const doc = win.document;
  const root = doc.documentElement;
  const he = (cfg.lang || root.getAttribute('lang') || '').toLowerCase().indexOf('he') === 0;
  const T = he
    ? { title: 'התאמות נגישות', size: 'גודל טקסט', contrast: 'ניגודיות גבוהה', links: 'הדגשת קישורים', font: 'גופן קריא', motion: 'עצירת אנימציות', cursor: 'סמן גדול', reset: 'איפוס הכל', stmt: 'הצהרת נגישות', open: 'פתיחת תפריט נגישות', dec: 'הקטנת טקסט', inc: 'הגדלת טקסט' }
    : { title: 'Accessibility', size: 'Text size', contrast: 'High contrast', links: 'Underline links', font: 'Readable font', motion: 'Pause animations', cursor: 'Large cursor', reset: 'Reset all', stmt: 'Accessibility statement', open: 'Open accessibility menu', dec: 'Decrease text', inc: 'Increase text' };
  const KEY = 'pom-a11y';
  let state: Record<string, unknown> = {};
  try { state = JSON.parse(win.localStorage.getItem(KEY) || '{}') || {}; } catch { state = {}; }
  const save = () => { try { win.localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* ignore */ } };
  const apply = () => {
    root.setAttribute('data-pom-a11y-font', state.font ? 'sans' : '');
    root.setAttribute('data-pom-a11y-contrast', state.contrast ? 'on' : '');
    root.setAttribute('data-pom-a11y-links', state.links ? 'on' : '');
    root.setAttribute('data-pom-a11y-motion', state.motion ? 'off' : '');
    root.setAttribute('data-pom-a11y-cursor', state.cursor ? 'on' : '');
    root.style.setProperty('--pom-a11y-fscale', String((state.fscale as number) || 1));
    root.setAttribute('data-pom-a11y-fscale', '');
  };
  const style = doc.createElement('style'); style.textContent = cfg.css; doc.head.appendChild(style);
  apply();

  const btn = doc.createElement('button');
  btn.className = 'pom-a11y-btn'; btn.type = 'button';
  btn.setAttribute('aria-label', T.open); btn.setAttribute('aria-expanded', 'false'); btn.innerHTML = cfg.icon;

  const panel = doc.createElement('div');
  panel.className = 'pom-a11y-panel'; panel.hidden = true;
  panel.setAttribute('role', 'dialog'); panel.setAttribute('aria-modal', 'true'); panel.setAttribute('aria-label', T.title);

  const heading = doc.createElement('h2'); heading.textContent = T.title; panel.appendChild(heading);

  const sizeRow = doc.createElement('div'); sizeRow.className = 'pom-a11y-row';
  const sizeLbl = doc.createElement('span'); sizeLbl.textContent = T.size;
  const sizeCtl = doc.createElement('span'); sizeCtl.className = 'pom-a11y-size';
  const mkSize = (sym: string, delta: number, al: string, resetAll?: boolean) => {
    const b = doc.createElement('button'); b.type = 'button'; b.textContent = sym; b.setAttribute('aria-label', al);
    b.onclick = () => {
      if (resetAll) state.fscale = 1;
      else state.fscale = Math.min(1.6, Math.max(0.9, (((state.fscale as number) || 1) + delta)));
      save(); apply();
    };
    return b;
  };
  sizeCtl.appendChild(mkSize('A−', -0.1, T.dec));
  sizeCtl.appendChild(mkSize('A', 0, 'A (reset)', true));
  sizeCtl.appendChild(mkSize('A+', 0.1, T.inc));
  sizeRow.appendChild(sizeLbl); sizeRow.appendChild(sizeCtl); panel.appendChild(sizeRow);

  const toggleRow = (label: string, key: string) => {
    const row = doc.createElement('div'); row.className = 'pom-a11y-row';
    const span = doc.createElement('span'); span.textContent = label;
    const t = doc.createElement('button'); t.type = 'button'; t.className = 'pom-a11y-toggle';
    t.textContent = state[key] ? '✓' : ''; t.setAttribute('aria-pressed', state[key] ? 'true' : 'false'); t.setAttribute('aria-label', label);
    t.onclick = () => { state[key] = !state[key]; t.setAttribute('aria-pressed', state[key] ? 'true' : 'false'); t.textContent = state[key] ? '✓' : ''; save(); apply(); };
    row.appendChild(span); row.appendChild(t); return row;
  };
  panel.appendChild(toggleRow(T.contrast, 'contrast'));
  panel.appendChild(toggleRow(T.links, 'links'));
  panel.appendChild(toggleRow(T.font, 'font'));
  panel.appendChild(toggleRow(T.motion, 'motion'));
  panel.appendChild(toggleRow(T.cursor, 'cursor'));

  const reset = doc.createElement('button'); reset.type = 'button'; reset.className = 'pom-a11y-reset'; reset.textContent = T.reset;
  reset.onclick = () => { state = {}; save(); apply(); doc.querySelectorAll('.pom-a11y-toggle').forEach((el) => { (el as HTMLElement).textContent = ''; el.setAttribute('aria-pressed', 'false'); }); };
  panel.appendChild(reset);

  const det = doc.createElement('details'); det.className = 'pom-a11y-statement';
  const sum = doc.createElement('summary'); sum.textContent = T.stmt; det.appendChild(sum);
  const sd = doc.createElement('div'); sd.innerHTML = he ? cfg.stmtHe : cfg.stmtEn; det.appendChild(sd);
  panel.appendChild(det);

  let isOpen = false;
  const openPanel = () => { panel.hidden = false; btn.setAttribute('aria-expanded', 'true'); isOpen = true; const f = panel.querySelector('button'); if (f) (f as HTMLElement).focus(); };
  const closePanel = () => { panel.hidden = true; btn.setAttribute('aria-expanded', 'false'); isOpen = false; btn.focus(); };
  btn.onclick = () => (isOpen ? closePanel() : openPanel());
  doc.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Escape' && isOpen) closePanel(); });
  doc.addEventListener('click', (e) => { const t = e.target as Node; if (isOpen && !panel.contains(t) && t !== btn && !btn.contains(t)) closePanel(); });

  // Visibility is OPT-IN. The button is only mounted when:
  //   • mode==='auto' (the pomela.app platform — Pomela owns its own IS-5568 duty), OR
  //   • the visitor previously opted in (persisted 'pom-a11y-enabled'), OR
  //   • the visitor invokes it — window.pomelaA11y.enable() (an app/builder can wire a
  //     trigger) or the Alt+Shift+A keyboard affordance.
  // Generated apps default to opt-in: the a11y.js + statement ship (SW-precached) but stay
  // hidden until enabled — the builder, not Pomela, owns each app's accessibility duty.
  // NOTE: individual settings (contrast/font size/…) are separate from visibility and always
  // re-apply from persisted state on load, so a returning user's chosen adjustments stick.
  let mounted = false;
  const showButton = () => {
    if (mounted) return; mounted = true;
    const put = () => { doc.body.appendChild(btn); doc.body.appendChild(panel); };
    if (doc.body) put(); else doc.addEventListener('DOMContentLoaded', put);
  };
  const hideButton = () => { mounted = false; if (btn.parentNode) btn.remove(); if (panel.parentNode) panel.remove(); };
  const persistEnabled = (v: boolean) => { try { win.localStorage.setItem('pom-a11y-enabled', v ? '1' : '0'); } catch { /* ignore */ } };
  const api = {
    enable() { persistEnabled(true); showButton(); openPanel(); },
    disable() { persistEnabled(false); closePanel(); hideButton(); },
    open() { showButton(); openPanel(); },
    toggle() { mounted ? api.disable() : api.enable(); },
  };
  (win as unknown as { pomelaA11y?: typeof api }).pomelaA11y = api;
  doc.addEventListener('keydown', (e) => {
    const k = e as KeyboardEvent;
    if (k.altKey && k.shiftKey && (k.key === 'A' || k.key === 'a')) { k.preventDefault(); api.toggle(); }
  });
  let optedIn = false;
  try { optedIn = win.localStorage.getItem('pom-a11y-enabled') === '1'; } catch { optedIn = false; }
  if (cfg.mode === 'auto' || optedIn) showButton();
}

/** The international accessibility symbol. */
const A11Y_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><circle cx="12" cy="4" r="2"/><path d="M4 8h16v2h-5v11h-2v-6h-2v6H9V10H4z"/></svg>';

function widgetCfg(opts: A11yWidgetOptions, defaultMode: 'auto' | 'optin'): WidgetCfg {
  return {
    email: opts.contactEmail,
    site: opts.siteName || '',
    lang: opts.lang || '',
    mode: opts.mode || defaultMode,
    css: A11Y_WIDGET_CSS,
    icon: A11Y_ICON,
    stmtHe: a11yStatementHtml({ ...opts, lang: 'he' }),
    stmtEn: a11yStatementHtml({ ...opts, lang: 'en' }),
  };
}

/**
 * Platform entry (pomela.app). Defaults to 'auto' — Pomela's own public, Israel-facing
 * site carries the visible option + statement, since Pomela owns its own IS 5568 duty.
 */
export function initA11yWidget(opts: A11yWidgetOptions, win: Window = window): void {
  mountA11yWidget(win, widgetCfg(opts, 'auto'));
}

/**
 * App entry: the self-contained a11y.js injected into every generated app. Defaults to
 * 'optin' — the widget + statement ship (and SW-precache) but stay hidden until the visitor
 * opts in. The builder owns each app's accessibility duty, so Pomela does not force a
 * visible widget onto every generated app.
 */
export function buildA11yWidgetScript(opts: A11yWidgetOptions): string {
  // mountA11yWidget is serialized via .toString(). The bundler (esbuild `keepNames`)
  // wraps named functions with a `__name(fn, "name")` helper that lives in the MODULE
  // scope and is therefore absent from the injected script — running it raw throws
  // `ReferenceError: __name is not defined` (caught only in a real browser, not happy-dom,
  // which imports the module normally). Prepend an identity shim so the helper resolves.
  // `__name` returns its first argument, so `var __name=function(t){return t}` is a correct
  // functional no-op regardless of whether the bundler emitted the helper at all.
  return `var __name=function(t){return t};(${mountA11yWidget.toString()})(window,${JSON.stringify(widgetCfg(opts, 'optin'))});`;
}
